import { z } from 'zod';

import { Id, IsoDateTime, Paise } from '../common/primitives';

export { Paise } from '../common/primitives';

// ── Enums ──────────────────────────────────────────────────────────────────

export const ReferralCampaignStatusSchema = z.enum(['DRAFT', 'ACTIVE', 'PAUSED', 'ENDED']);
export type ReferralCampaignStatus = z.infer<typeof ReferralCampaignStatusSchema>;

export const ReferralRewardKindSchema = z.enum(['REFERRER_CASHBACK', 'REFEREE_CASHBACK', 'MLM_COMMISSION']);
export type ReferralRewardKind = z.infer<typeof ReferralRewardKindSchema>;

export const ReferralRewardStatusSchema = z.enum(['PENDING', 'ISSUED', 'REVERSED', 'EXPIRED']);
export type ReferralRewardStatus = z.infer<typeof ReferralRewardStatusSchema>;

export const TeamStatusSchema = z.enum(['ACTIVE', 'ARCHIVED']);
export type TeamStatus = z.infer<typeof TeamStatusSchema>;

export const TeamMembershipRoleSchema = z.enum(['LEADER', 'MEMBER']);
export type TeamMembershipRole = z.infer<typeof TeamMembershipRoleSchema>;

export const RankTierCodeSchema = z.enum(['BRONZE', 'SILVER', 'GOLD', 'PLATINUM', 'DIAMOND']);
export type RankTierCode = z.infer<typeof RankTierCodeSchema>;

// ── Campaign ───────────────────────────────────────────────────────────────

export const ReferralCampaignSchema = z.object({
  id: Id,
  code: z.string(),
  name: z.string(),
  status: ReferralCampaignStatusSchema,
  referrerRewardPaise: Paise,
  refereeRewardPaise: Paise,
  minOrderValuePaise: Paise,
  maxReferralsPerReferrer: z.number().int(),
  rewardExpiryDays: z.number().int(),
  priority: z.number().int(),
  startsAt: IsoDateTime,
  endsAt: IsoDateTime.nullable(),
  notes: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type ReferralCampaign = z.infer<typeof ReferralCampaignSchema>;

export const UpsertReferralCampaignRequest = z
  .object({
    code: z.string().trim().regex(/^[A-Z0-9_-]{2,30}$/, 'Code must be uppercase alphanumeric'),
    name: z.string().trim().min(2).max(120),
    referrerRewardPaise: Paise.refine((v) => v >= 0),
    refereeRewardPaise: Paise.refine((v) => v >= 0),
    minOrderValuePaise: Paise.optional(),
    maxReferralsPerReferrer: z.number().int().min(1).max(10_000).optional(),
    rewardExpiryDays: z.number().int().min(1).max(3650).optional(),
    priority: z.number().int().min(0).max(100).optional(),
    startsAt: IsoDateTime.optional(),
    endsAt: IsoDateTime.optional(),
    status: ReferralCampaignStatusSchema.optional(),
    notes: z.string().trim().max(2000).optional(),
  })
  .strict();
export type UpsertReferralCampaignRequest = z.infer<typeof UpsertReferralCampaignRequest>;

// ── Referral code ─────────────────────────────────────────────────────────

export const MyReferralCodeSchema = z.object({
  userId: Id,
  code: z.string(),
  createdAt: IsoDateTime,
  /// Lifetime stats — handy for the referral dashboard card.
  totalClicks: z.number().int().nonnegative(),
  totalConversions: z.number().int().nonnegative(),
  totalRewardPaise: Paise,
});
export type MyReferralCode = z.infer<typeof MyReferralCodeSchema>;

// ── Click + conversion ───────────────────────────────────────────────────

export const RecordReferralClickRequest = z
  .object({
    code: z.string().trim().min(2).max(64),
    anonymousId: z.string().trim().max(120).optional(),
    source: z.string().trim().max(120).optional(),
    metadata: z.record(z.unknown()).optional(),
  })
  .strict();
export type RecordReferralClickRequest = z.infer<typeof RecordReferralClickRequest>;

export const AttachReferralRequest = z
  .object({
    /// The referral code the new user signed up with.
    code: z.string().trim().min(2).max(64),
  })
  .strict();
export type AttachReferralRequest = z.infer<typeof AttachReferralRequest>;

export const RecordReferralConversionRequest = z
  .object({
    refereeUserId: Id,
    orderValuePaise: Paise.refine((v) => v > 0),
    /// Used for idempotency by callers like the orders module.
    orderRefId: z.string().trim().min(1).max(64).optional(),
  })
  .strict();
export type RecordReferralConversionRequest = z.infer<typeof RecordReferralConversionRequest>;

export const ReferralConversionSchema = z.object({
  id: Id,
  code: z.string(),
  campaignId: Id.nullable(),
  referrerUserId: Id,
  refereeUserId: Id,
  orderCount: z.number().int(),
  orderValuePaise: Paise,
  firstOrderAt: IsoDateTime.nullable(),
  lastOrderAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
});
export type ReferralConversion = z.infer<typeof ReferralConversionSchema>;

export const ReferralRewardSchema = z.object({
  id: Id,
  campaignId: Id.nullable(),
  conversionId: Id.nullable(),
  beneficiaryId: Id,
  kind: ReferralRewardKindSchema,
  status: ReferralRewardStatusSchema,
  amountPaise: Paise,
  walletCreditId: Id.nullable(),
  mlmLevel: z.number().int().nullable(),
  notes: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type ReferralReward = z.infer<typeof ReferralRewardSchema>;

// ── Teams ─────────────────────────────────────────────────────────────────

export const TeamSchema = z.object({
  id: Id,
  code: z.string(),
  name: z.string(),
  leaderUserId: Id,
  status: TeamStatusSchema,
  gmvLifetimePaise: Paise,
  gmv30dPaise: Paise,
  activeMemberCount: z.number().int().nonnegative(),
  salesGoalPaise: Paise,
  goalCycle: z.string(),
  createdAt: IsoDateTime,
});
export type Team = z.infer<typeof TeamSchema>;

export const CreateTeamRequest = z
  .object({
    code: z.string().trim().regex(/^[A-Z0-9_-]{2,30}$/, 'Code must be uppercase alphanumeric'),
    name: z.string().trim().min(2).max(120),
    leaderUserId: Id,
    salesGoalPaise: Paise.optional(),
    goalCycle: z.enum(['DAY', 'WEEK', 'MONTH', 'QUARTER', 'YEAR']).optional(),
  })
  .strict();
export type CreateTeamRequest = z.infer<typeof CreateTeamRequest>;

export const AddTeamMemberRequest = z
  .object({
    userId: Id,
    role: TeamMembershipRoleSchema.default('MEMBER'),
  })
  .strict();
export type AddTeamMemberRequest = z.infer<typeof AddTeamMemberRequest>;

export const TeamMembershipSchema = z.object({
  id: Id,
  teamId: Id,
  userId: Id,
  role: TeamMembershipRoleSchema,
  joinedAt: IsoDateTime,
  leftAt: IsoDateTime.nullable(),
});
export type TeamMembership = z.infer<typeof TeamMembershipSchema>;

// ── Ranks ─────────────────────────────────────────────────────────────────

export const RankTierSchema = z.object({
  id: Id,
  code: RankTierCodeSchema,
  ordinal: z.number().int(),
  minGmvLifetimePaise: Paise,
  minDirectReferrals: z.number().int(),
  commissionMultiplierBps: z.number().int(),
  active: z.boolean(),
});
export type RankTier = z.infer<typeof RankTierSchema>;

export const UpsertRankTierRequest = z
  .object({
    code: RankTierCodeSchema,
    ordinal: z.number().int().min(0).max(100),
    minGmvLifetimePaise: Paise,
    minDirectReferrals: z.number().int().min(0),
    commissionMultiplierBps: z.number().int().min(0).max(50_000),
    active: z.boolean().optional(),
  })
  .strict();
export type UpsertRankTierRequest = z.infer<typeof UpsertRankTierRequest>;

export const UserRankSchema = z.object({
  userId: Id,
  tierCode: RankTierCodeSchema,
  gmvLifetimePaise: Paise,
  directReferrals: z.number().int(),
  promotedAt: IsoDateTime.nullable(),
  computedAt: IsoDateTime,
});
export type UserRank = z.infer<typeof UserRankSchema>;

// ── MLM commission rules ──────────────────────────────────────────────────

export const MlmCommissionRuleSchema = z.object({
  id: Id,
  level: z.number().int(),
  rateBps: z.number().int(),
  active: z.boolean(),
});
export type MlmCommissionRule = z.infer<typeof MlmCommissionRuleSchema>;

export const UpsertMlmRuleRequest = z
  .object({
    level: z.number().int().min(1).max(20),
    rateBps: z.number().int().min(0).max(5000),
    active: z.boolean().optional(),
    notes: z.string().trim().max(500).optional(),
  })
  .strict();
export type UpsertMlmRuleRequest = z.infer<typeof UpsertMlmRuleRequest>;

// ── Rank engine run ───────────────────────────────────────────────────────

export const RunRankEngineRequest = z
  .object({
    limit: z.number().int().min(1).max(10_000).default(1000),
  })
  .strict();
export type RunRankEngineRequest = z.infer<typeof RunRankEngineRequest>;

export const RankEngineResultSchema = z.object({
  scanned: z.number().int().nonnegative(),
  promotedCount: z.number().int().nonnegative(),
  demotedCount: z.number().int().nonnegative(),
  durationMs: z.number().int().nonnegative(),
});
export type RankEngineResult = z.infer<typeof RankEngineResultSchema>;
